/**
 * SearchIndexMonitor Component
 * 
 * Admin component for monitoring and managing search indexes.
 * Shows health, statistics, and provides rebuild functionality.
 * 
 * Reusable for any search index - pass in configuration.
 * 
 * Usage:
 * <SearchIndexMonitor
 *   title="Property Search Index"
 *   searchIndexConfig={PropertySearchIndex}
 *   onRebuildComplete={() => console.log('Rebuild done')}
 * />
 */

import React, { useState, useEffect } from 'react';
import { useSearchIndex } from '../../../hooks/useSearchIndex';
import { Button } from '../index';
import { FaSync, FaCheckCircle, FaExclamationTriangle, FaClock, FaDatabase } from 'react-icons/fa';
import './SearchIndexMonitor.scss';

export const SearchIndexMonitor = ({ 
  title = 'Search Index',
  searchIndexConfig,
  onRebuildComplete
}) => {
  const {
    stats,
    loadingStats,
    refreshStats,
    rebuild,
    rebuilding,
    healthCheck,
    error
  } = useSearchIndex(searchIndexConfig, { loadStatsOnMount: true });

  const [health, setHealth] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // Load health check on mount
  useEffect(() => {
    const checkHealth = async () => {
      const result = await healthCheck();
      setHealth(result);
    };
    checkHealth();
  }, [healthCheck]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown';
    }
  };

  // Handle rebuild
  const handleRebuild = async (clearFirst = false) => {
    setShowConfirm(false);
    try {
      const result = await rebuild({ clearFirst });
      if (onRebuildComplete) {
        onRebuildComplete(result);
      }
    } catch (err) {
      // Error handled by hook
    }
  };

  // Health status indicator
  const getHealthStatus = () => {
    if (!health) return { color: '#6b7280', icon: FaClock, text: 'Checking...' };
    if (health.healthy) return { color: '#10b981', icon: FaCheckCircle, text: 'Healthy' };
    return { color: '#ef4444', icon: FaExclamationTriangle, text: 'Unhealthy' };
  };

  const healthStatus = getHealthStatus();

  return (
    <div className="search-index-monitor">
      <div className="search-index-monitor__header">
        <div className="search-index-monitor__title">
          <FaDatabase className="search-index-monitor__icon" />
          <h3>{title}</h3>
        </div>
        <div className="search-index-monitor__actions">
          <Button
            variant="secondary"
            size="small"
            onClick={refreshStats}
            disabled={loadingStats || rebuilding}
          >
            <FaSync className={loadingStats ? 'spinning' : ''} /> Refresh
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={() => setShowConfirm(true)}
            disabled={rebuilding || loadingStats}
          >
            <FaSync /> Rebuild Index
          </Button>
        </div>
      </div>

      {error && (
        <div className="search-index-monitor__error">
          <FaExclamationTriangle />
          <span>{error}</span>
        </div>
      )}

      <div className="search-index-monitor__content">
        {/* Health Status */}
        <div className="search-index-monitor__section">
          <h4>Health Status</h4>
          <div className="search-index-monitor__stat search-index-monitor__stat--health">
            <healthStatus.icon style={{ color: healthStatus.color }} />
            <span style={{ color: healthStatus.color }}>{healthStatus.text}</span>
            {health && health.message && (
              <span className="search-index-monitor__stat-detail">{health.message}</span>
            )}
          </div>
        </div>

        {/* Statistics */}
        {stats && (
          <>
            <div className="search-index-monitor__section">
              <h4>Index Statistics</h4>
              <div className="search-index-monitor__stats-grid">
                <div className="search-index-monitor__stat">
                  <span className="search-index-monitor__stat-label">Total Documents</span>
                  <span className="search-index-monitor__stat-value">
                    {stats.totalDocuments?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="search-index-monitor__stat">
                  <span className="search-index-monitor__stat-label">Average Tokens</span>
                  <span className="search-index-monitor__stat-value">
                    {stats.averageTokens || 0}
                  </span>
                </div>
                <div className="search-index-monitor__stat">
                  <span className="search-index-monitor__stat-label">Max Tokens</span>
                  <span className="search-index-monitor__stat-value">
                    {stats.maxTokens || 0}
                  </span>
                </div>
                <div className="search-index-monitor__stat">
                  <span className="search-index-monitor__stat-label">Min Tokens</span>
                  <span className="search-index-monitor__stat-value">
                    {stats.minTokens || 0}
                  </span>
                </div>
              </div>
            </div>

            <div className="search-index-monitor__section">
              <h4>Index Details</h4>
              <div className="search-index-monitor__details">
                <div className="search-index-monitor__detail-row">
                  <span className="search-index-monitor__detail-label">Source Collection:</span>
                  <code>{stats.sourceCollection}</code>
                </div>
                <div className="search-index-monitor__detail-row">
                  <span className="search-index-monitor__detail-label">Index Collection:</span>
                  <code>{stats.indexCollection}</code>
                </div>
                <div className="search-index-monitor__detail-row">
                  <span className="search-index-monitor__detail-label">Last Updated:</span>
                  <span>{formatDate(stats.lastUpdated)}</span>
                </div>
                <div className="search-index-monitor__detail-row">
                  <span className="search-index-monitor__detail-label">Total Tokens:</span>
                  <span>{stats.totalTokens?.toLocaleString() || 0}</span>
                </div>
              </div>
            </div>
          </>
        )}

        {loadingStats && !stats && (
          <div className="search-index-monitor__loading">
            <FaSync className="spinning" />
            <span>Loading statistics...</span>
          </div>
        )}

        {rebuilding && (
          <div className="search-index-monitor__rebuilding">
            <FaSync className="spinning" />
            <span>Rebuilding index... This may take a few minutes.</span>
          </div>
        )}
      </div>

      {/* Rebuild Confirmation Modal */}
      {showConfirm && (
        <div className="search-index-monitor__modal-overlay" onClick={() => setShowConfirm(false)}>
          <div className="search-index-monitor__modal" onClick={e => e.stopPropagation()}>
            <h3>Rebuild Search Index?</h3>
            <p>
              This will rebuild the entire search index from the source collection.
              For large collections, this may take several minutes.
            </p>
            {stats && (
              <p className="search-index-monitor__modal-info">
                <strong>{stats.totalDocuments?.toLocaleString()}</strong> documents will be reindexed.
              </p>
            )}
            <div className="search-index-monitor__modal-actions">
              <Button variant="secondary" onClick={() => setShowConfirm(false)}>
                Cancel
              </Button>
              <Button 
                variant="danger" 
                onClick={() => handleRebuild(true)}
                title="Clear all existing index documents first, then rebuild from scratch"
              >
                Clear & Rebuild
              </Button>
              <Button 
                variant="primary" 
                onClick={() => handleRebuild(false)}
                title="Overwrite existing index documents (faster)"
              >
                Rebuild
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchIndexMonitor;
